import React from "react";
import clsx from "clsx";

export interface ConsentTableColumn<T> {
  key: string;
  header: React.ReactNode;
  // Applied to the column's header and body cells, e.g. alignment or a fixed width. Override the 24px side padding
  // with `pl-`/`pr-` classes, which reliably win over the base `px-`.
  className?: string;
  render: (item: T) => React.ReactNode;
}

interface ConsentTableProps<T> {
  columns: ConsentTableColumn<T>[];
  // Rows to show; none while a caller is still loading them.
  data: T[] | undefined;
  keyExtractor: (item: T) => string;
  // Shown in place of the rows when there are none.
  emptyState?: React.ReactNode;
  // Join this table to the one above or below it, so the two read as one card (Global consent's default and
  // overrides tables).
  joinedTop?: boolean;
  joinedBottom?: boolean;
}

/**
 * A settings table styled like the Vendor List tables: a white card with small uppercase column labels and 14px rows.
 * Overflow stays visible so dropdowns inside a row aren't cut off, so the header rounds its own top corners.
 */
export const ConsentTable = <T,>({
  columns,
  data,
  keyExtractor,
  emptyState,
  joinedTop = false,
  joinedBottom = false,
}: ConsentTableProps<T>) => (
  <div
    className={clsx(
      "border border-solid border-neutral-200 bg-white",
      joinedTop ? "border-t-0" : "rounded-t-xl",
      !joinedBottom && "rounded-b-xl shadow-[0px_1px_2px_0px_#1018280D]",
    )}
  >
    <table className="w-full border-separate border-spacing-0">
      <thead>
        <tr>
          {columns.map((column) => (
            <th
              key={column.key}
              scope="col"
              className={clsx(
                "border-0 border-b border-solid border-neutral-200 bg-neutral-0 px-[24px] py-[11px] text-left align-middle text-[12px] font-medium uppercase leading-[16px] tracking-[0.4px] text-neutral-600",
                !joinedTop && "first:rounded-tl-[11px] last:rounded-tr-[11px]",
                column.className,
              )}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {!data?.length ? (
          <tr>
            <td colSpan={columns.length} className="px-[24px] py-[16px] text-center">
              {emptyState}
            </td>
          </tr>
        ) : (
          data.map((item, rowIndex) => (
            <tr key={keyExtractor(item)}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={clsx(
                    "px-[24px] py-[16px] align-middle text-[14px] leading-[18px] text-neutral-900",
                    rowIndex > 0 && "border-0 border-t border-solid border-neutral-200",
                    column.className,
                  )}
                >
                  {column.render(item)}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);
